/**
파일명: useContactForm.js
설명: 문의 등록 폼 상태와 제출 흐름을 관리하는 커스텀 훅입니다.
*/

import { useState } from 'react';
import {
  createInitialFormState,
  initialErrors,
  inquiryApiUrl,
} from '../constants/contactFormConstants';
import { inquiryTypes } from '../constants/supportData';
import { validateFormState } from '../utils/contactFormValidation';

const initialFormState = createInitialFormState(inquiryTypes[0] ?? '');

// [비즈니스] 전송 실패 안내는 원인에 따라 달라야 합니다.
//            서버 장애는 다른 연락 수단을 안내하고,
//            횟수 제한은 잠시 후 재시도하면 되는 상황이므로 불필요한 전화를 유발하지 않습니다.
const SEND_FAIL_MESSAGE = '문의 전송에 실패했습니다. 대표 전화로 문의 바랍니다.';
const RATE_LIMIT_MESSAGE = '요청이 너무 잦습니다. 잠시 후 다시 시도해 주세요.';

export function useContactForm(onNavigate) {
  const [isWriting, setIsWriting] = useState(true);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendFailModalOpen, setIsSendFailModalOpen] = useState(false);
  const [sendFailMessage, setSendFailMessage] = useState(SEND_FAIL_MESSAGE);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  const [errors, setErrors] = useState(initialErrors);
  const [formState, setFormState] = useState(initialFormState);

  /**
  // [비즈니스] 사용자가 입력한 폼 값이 즉시 반영되어야 작성 경험이 자연스럽습니다.
  // [기능] input/checkbox 변경값을 formState에 반영하고 해당 필드 에러를 초기화합니다.
  */
  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormState((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setErrors((current) => ({ ...current, [name]: '' }));
  };

  /**
  // [비즈니스] 문의 본문 입력 에디터 상태를 폼 검증과 일관되게 관리합니다.
  // [기능] message 값을 갱신하고 message 필드 에러를 초기화합니다.
  */
  const handleMessageChange = (value) => {
    setFormState((current) => ({ ...current, message: value }));
    setErrors((current) => ({ ...current, message: '' }));
  };

  /**
  // [비즈니스] 작성을 취소할 경우 홈으로 이동합니다.
  // [기능] onNavigate가 제공되면 홈으로 이동하고, 없으면 isWriting을 초기화합니다.
  */
  const closeForm = () => {
    if (typeof onNavigate === 'function') {
      onNavigate('/');
    } else {
      setIsWriting(false);
    }
  };

  /**
  // [비즈니스] 전송 실패 안내 모달을 닫아 사용자가 재시도 흐름으로 돌아갈 수 있게 합니다.
  // [기능] 실패 모달 열림 상태를 false로 변경합니다.
  */
  const closeSendFailModal = () => {
    setIsSendFailModalOpen(false);
  };

  /**
  // [비즈니스] 문의 접수 성공 모달을 닫고 사용자가 추가 문의를 작성할 수 있게 합니다.
  // [기능] 성공 모달 열림 상태를 false로 변경합니다.
  */
  const closeSuccessModal = () => {
    setIsSuccessModalOpen(false);
  };

  /**
  // [비즈니스] 개인정보 동의 체크 시 검증 메시지를 즉시 해제해 입력 피드백을 개선합니다.
  // [기능] privacyAgreed 필드 에러 메시지를 빈 값으로 갱신합니다.
  */
  const clearPrivacyError = () => {
    setErrors((current) => ({ ...current, privacyAgreed: '' }));
  };

  /**
  // [비즈니스] 등록 전 클라이언트 검증으로 사용자 오류를 즉시 알려 재입력 비용을 낮춥니다.
  // [기능] 현재 폼 상태를 검증하고 에러 상태를 갱신한 뒤 유효 여부와 평문 메시지를 반환합니다.
  */
  const validateForm = () => {
    const { nextErrors, isValid, plainMessage } = validateFormState(formState);
    setErrors(nextErrors);
    return { isValid, plainMessage };
  };

  /**
  // [비즈니스] 고객 문의를 서버에 접수해 관리자에게 이메일로 전달합니다. DB에 저장하지 않습니다.
  // [기능] 폼 제출 이벤트를 처리해 검증 후 문의 생성 API를 호출합니다.
  // [기술] 중복 제출 방지 플래그와 성공/실패 모달 분기를 수행합니다.
  */
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) {
      return;
    }

    const { isValid } = validateForm();
    if (!isValid) {
      return;
    }

    const requestBody = {
      inquiryType: formState.inquiryType.trim(),
      title: formState.title.trim(),
      company: formState.company.trim(),
      managerName: formState.manager.trim(),
      phone: formState.contact.trim(),
      email: formState.email.trim(),
      // Quill HTML을 그대로 전송해 메일에서 서식(리스트·굵기·색상 등)이 유지되도록 함
      // plainMessage는 빈값 검증에만 사용하고 실제 전송값은 HTML 원본을 사용
      content: formState.message,
    };

    try {
      setIsSubmitting(true);
      setIsSendFailModalOpen(false);

      const response = await fetch(inquiryApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      // [비즈니스] 429는 횟수 제한에 걸린 정상 사용자이므로 장애와 구분해 안내합니다.
      if (response.status === 429) {
        setSendFailMessage(RATE_LIMIT_MESSAGE);
        setIsSendFailModalOpen(true);
        return;
      }

      if (!response.ok || response.status >= 500) {
        throw new Error(`request failed: ${response.status}`);
      }

      await response.json().catch(() => ({}));

      setIsSuccessModalOpen(true);
      setErrors(initialErrors);
      setFormState(initialFormState);
    } catch (error) {
      setSendFailMessage(SEND_FAIL_MESSAGE);
      setIsSendFailModalOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    clearPrivacyError,
    closeForm,
    closeSendFailModal,
    closeSuccessModal,
    errors,
    formState,
    handleChange,
    handleMessageChange,
    handleSubmit,
    isSendFailModalOpen,
    isSubmitting,
    isSuccessModalOpen,
    isWriting,
    sendFailMessage,
  };
}
